import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CreateExperimentDto,
  experimentsApi,
  type UpdateExperimentStatusDto,
} from '../experiments-api';

const KEY = ['voice-agent-experiments'] as const;

export function useExperiments(agentId: string, organizationId: string) {
  return useQuery({
    queryKey: [...KEY, agentId, organizationId],
    queryFn: () => experimentsApi.list(agentId, organizationId),
    enabled: !!agentId && !!organizationId,
  });
}

export function useExperimentReport(agentId: string, experimentId: string, organizationId: string) {
  return useQuery({
    queryKey: [...KEY, agentId, experimentId, 'report', organizationId],
    queryFn: () => experimentsApi.getReport(agentId, experimentId, organizationId),
    enabled: !!agentId && !!experimentId && !!organizationId,
    refetchInterval: 30_000,
  });
}

export function useCreateExperiment(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateExperimentDto) => experimentsApi.create(agentId, dto),
    onSuccess: (_data, dto) => {
      qc.invalidateQueries({ queryKey: [...KEY, agentId, dto.organizationId] });
    },
  });
}

export function useUpdateExperimentStatus(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ experimentId, dto }: { experimentId: string; dto: UpdateExperimentStatusDto }) =>
      experimentsApi.updateStatus(agentId, experimentId, dto),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [...KEY, agentId, vars.dto.organizationId] });
    },
  });
}
